<template>
    <div class="color-selection">
        <div class="color-selection__body">
            <div class="tw-mx-auto">
                <h3 class="color-selection__title tw-mt-8 lg:tw-mt-14 headline-md tw-font-bold tw-text-center">
                    {{ $t('configure-car.select-color') }}
                </h3>
                <div
                    v-if="selectedColor && selectedColor.active"
                    class="color-selection__price tw-flex tw-justify-between lg:tw-justify-center tw-items-center tw-text-base-subtle tw-w-full tw-mt-6 tw-mb-4 lg:tw-mt-10 tw-px-6 lg:tw-gap-4"
                >
                    <span class="tw-text-xs lg:tw-text-base tw-leading-xs">
                        {{ selectedColor.description }}
                    </span>
                    <span class="tw-text-base tw-leading-base tw-font-semibold">
                        + {{ selectedColor.finalPrice }}
                    </span>
                </div>
            </div>
            <loading-wrapper v-if="colorList.length" :loading="colorSelectionIsBusy">
                <dk-color-picker
                    :colors="colorList"
                    :show-color-name="false"
                    :disclaimer="$t('configure-car.disclaimer')"
                    :max-landing="isMobile ? 5 : 10"
                    has-dealer-selection-layout
                    @item-click="onColorSelection"
                    @order-change="onColorsOrderChange"
                />
            </loading-wrapper>
            <div
                v-else
                class="color-selection__fake-color-picker dst tw-grid tw-grid-cols-3 max-lg:tw-px-10 tw-gap-y-3 tw-gap-x-4 lg:tw-flex lg:tw-items-center lg:tw-justify-center lg:tw-gap-14 tw-mt-6 tw-mb-4 lg:tw-mt-10"
            >
                <label
                    v-for="(color, i) in fakeColors"
                    :key="i"
                    class="color-selection__fake-color tw-inline-flex tw-items-center"
                >
                    <input
                        v-model="smokeColor"
                        class="tw-m-0"
                        type="radio"
                        name="smoke-color"
                        :value="$t(`configure-car.smoke-color.${color}`)"
                        @change="onFakeColorSelection"
                    >
                    <span class="tw-ml-2 tw-text-basic tw-leading-base tw-text-base">
                        {{ $t(`configure-car.smoke-color.${color}`) }}
                    </span>
                </label>
            </div>
            <p v-if="!colorList.length" class="tw-mt-4 tw-text-2xs tw-leading-2xs tw-text-base-subtle tw-text-center">
                {{ $t('configure-car.disclaimer') }}
            </p>
        </div>
    </div>
</template>

<script>
/**
 * A car configurator component
 */
import { isMobile } from '@v4/common/utils/media-query';

export default {
    name: 'ColorSelection',
    components: {
        LoadingWrapper: () => ({
            component: import(
                /* webpackChunkName: "LoadingWrapper-vue" */ '@v4/common/components/atoms/LoadingWrapper'
            ),
        }),
        DkColorPicker: () => ({
            component: import(
                /* webpackChunkName: "DkColorPicker-vue" */ '@v4/common/components/molecules/DkColorPicker.vue'
            ),
        }),
    },
    props: {
        /**
         * Fallback image to use when empty colors
         */
        colorImage: {
            type: String,
            default: '',
        },
        /**
         * Color items
         */
        colors: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            colorInteracted: false,
            colorSelectionIsBusy: false,
            smokeColor: '',
            colorList: this.colors.map((color, index) => ({
                ...color,
                ...(index === 0 && { active: true }),
                ...(index > 0 && { active: false }),
            })),
            isMobile: isMobile(),
            fakeColors: ['black', 'white', 'red', 'gray', 'blue', 'others'],
        };
    },
    computed: {
        selectedColor() {
            return this.colorList.find((color) => color.active) || {};
        },
    },
    methods: {
        onColorSelection(index) {
            const item = this.colorList[index];

            if (item && item.active) {
                return;
            }

            this.colorSelectionIsBusy = this.colorInteracted && index > 0;
            this.colorInteracted = true;

            const color = this.setActiveByPropertyAndIndex('colorList', index);

            /**
             * Fired when a color it's selected
             *
             * @event color-select
             * @type {object}
             * @property {object} color - Color item
             */
            this.$emit('color-select', color);
        },
        onImageLoad() {
            this.colorSelectionIsBusy = false;
        },
        setActiveByPropertyAndIndex(property, index) {
            const foundItem = (this[property] && this[property][index]) || null;

            if (!foundItem) {
                return;
            }

            this[property] = this[property].map((item) => ({ ...item, active: false }));
            this.$set(this[property], index, { ...foundItem, active: true });

            return foundItem;
        },
        onColorsOrderChange(index) {
            const [activeObject] = this.colorList.splice(index, 1);
            this.colorList.unshift(activeObject);
            this.onColorSelection(0);
        },
        onFakeColorSelection() {
            this.$emit('fake-color-select', this.smokeColor);
        },
    },
};
</script>
